'use client';

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  IconButton,
  Chip,
  Popover,
  Button,
  Checkbox,
  ListSubheader,
  TextField,
} from '@mui/material';
import { toast } from 'react-hot-toast';
import './CreateNewBannerModal.css';
import BannerIcon from '../../../../assets/Admin/Banner/upload.svg'; // Path to the upload icon
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import { Calendar } from 'iconsax-react';
import { axiosInstance } from '../../../../utils/tokenUtils';
import MobileBannerPreview from '../MobileBannerPreview/MobileBannerPreview';
import '../MobileBannerPreview/MobileBannerPreview.css';
import BannerImageCropper from '../BannerImageCropper/BannerImageCropper';

// Filter options for each category
const filterOptions = {
  instructor_type: ['Academic', 'Creative Arts'],
  level_of_teachings: [
    'elementary',
    'middle_school',
    'high_school',
    'university',
    'beginner',
    'intermediate',
    'professional',
  ],
};

// Mapping for instructor types to match API enums
const instructorTypeApiMap = {
  Academic: 'academic_teacher',
  'Creative Arts': 'creative_teacher',
};

// Mapping for instructor types to the subjects `subject_type` filter
const subjectTypeApiMap = {
  Academic: 'academic',
  'Creative Arts': 'creative',
};

// Number of subjects fetched per page from the paginated subjects API
const SUBJECT_PAGE_SIZE = 30;

// Function to format text with proper capitalization and spacing
const formatText = text => {
  // Replace underscores with spaces and capitalize each word
  return text
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Custom Calendar Component
const CustomCalendar = ({ value, onChange, minDate }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(value ? new Date(value) : null);

  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];

  const daysOfWeek = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

  // Get days in month
  const getDaysInMonth = date => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const handleDateClick = date => {
    if (!date) return;

    // Check if date is before minDate
    if (minDate) {
      const [year, month, day] = minDate.split('-').map(Number);
      const min = new Date(year, month - 1, day); // month is 0-indexed
      if (date < min) return;
    }

    setSelectedDate(date);
    // Fix: Use date-only formatting without timezone conversion
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    onChange(formattedDate);
  };

  const handleMonthChange = direction => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleYearChange = direction => {
    const newDate = new Date(currentDate);
    newDate.setFullYear(currentDate.getFullYear() + direction);
    setCurrentDate(newDate);
  };

  const handleToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
    // Fix: Use date-only formatting without timezone conversion
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    onChange(formattedDate);
  };

  const handleClear = () => {
    setSelectedDate(null);
    onChange('');
  };

  const isDateDisabled = date => {
    if (!date || !minDate) return false;
    // Fix: Parse minDate as local date to avoid timezone issues
    const [year, month, day] = minDate.split('-').map(Number);
    const min = new Date(year, month - 1, day); // month is 0-indexed
    return date < min;
  };

  const isToday = date => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = date => {
    if (!date || !selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="banner-custom-calendar">
      {/* Calendar Header */}
      <div className="banner-calendar-header">
        <div className="banner-month-year-selector">
          <div className="banner-month-selector">
            <span className="banner-month-year-text">
              {months[currentDate.getMonth()]}, {currentDate.getFullYear()}
            </span>
            <div className="banner-navigation-buttons">
              <IconButton
                size="small"
                onClick={() => handleMonthChange(-1)}
                className="banner-nav-button"
              >
                <KeyboardArrowUpIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleMonthChange(1)}
                className="banner-nav-button"
              >
                <KeyboardArrowDownIcon fontSize="small" />
              </IconButton>
            </div>
          </div>
        </div>
        <div className="banner-year-navigation">
          <IconButton
            size="small"
            onClick={() => handleYearChange(-1)}
            className="banner-nav-button"
          >
            <KeyboardArrowLeftIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleYearChange(1)}
            className="banner-nav-button"
          >
            <KeyboardArrowRightIcon fontSize="small" />
          </IconButton>
        </div>
      </div>

      {/* Days of Week Header */}
      <div className="banner-calendar-days-header">
        {daysOfWeek.map(day => (
          <div key={day} className="banner-day-header">
            {day}
          </div>
        ))}
      </div>

      {/* Calendar Days Grid */}
      <div className="banner-calendar-grid">
        {days.map((date, index) => (
          <div
            key={index}
            className={`banner-calendar-day ${!date ? 'empty' : ''} ${
              isSelected(date) ? 'selected' : ''
            } ${isToday(date) ? 'today' : ''} ${isDateDisabled(date) ? 'disabled' : ''}`}
            onClick={() => handleDateClick(date)}
          >
            {date ? date.getDate() : ''}
          </div>
        ))}
      </div>

      {/* Calendar Footer */}
      <div className="banner-calendar-footer">
        <Button onClick={handleClear} className="banner-calendar-clear-btn" size="small">
          Clear
        </Button>
        <Button onClick={handleToday} className="banner-calendar-today-btn" size="small">
          Today
        </Button>
      </div>
    </div>
  );
};

const CreateNewBannerModel = ({ onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    placement: '',
    navigate_to: '',
    image: null,
    image_url: '',
    expiry_date: '',
  });

  const [loading, setLoading] = useState(false);
  const [imageUploading, setImageUploading] = useState(false);

  // Source image (object URL) shown in the interactive cropper before upload.
  const [cropSource, setCropSource] = useState('');

  // States for multiselect functionality
  const [subjects, setSubjects] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [instructorType, setInstructorType] = useState('');

  // Subjects pagination state (infinite scroll on the dropdown)
  const [subjectPage, setSubjectPage] = useState(1);
  const [subjectTotalCount, setSubjectTotalCount] = useState(0);
  const [subjectHasMore, setSubjectHasMore] = useState(true);
  const [subjectLoadingMore, setSubjectLoadingMore] = useState(false);
  // Subjects search (server-side, debounced)
  const [subjectSearch, setSubjectSearch] = useState('');
  const [debouncedSubjectSearch, setDebouncedSubjectSearch] = useState('');

  // Target audience card states
  const [selectedCards, setSelectedCards] = useState({
    instructor: false,
    learner: false,
    gender: false,
    country: false,
    city: false,
  });

  // Gender selection
  const [selectedGender, setSelectedGender] = useState('');

  // Country / City data & selection
  const [countries, setCountries] = useState([]);
  const [selectedCountries, setSelectedCountries] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);

  // New states for user type and navigation
  const [navigationType, setNavigationType] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  const getLevelsForInstructorType = type => {
    if (type === 'Academic') {
      return ['elementary', 'middle_school', 'high_school', 'university'];
    }
    if (type === 'Creative Arts') {
      return ['beginner', 'intermediate', 'professional'];
    }
    return filterOptions.level_of_teachings;
  };

  const inAppThirdPageOption = selectedCards.learner ? 'Wallet' : 'Set Availability';

  useEffect(() => {
    if (navigationType !== 'inapp') return;
    if (selectedCards.learner && formData.navigate_to === 'Set Availability') {
      setFormData(prev => ({ ...prev, navigate_to: '' }));
    } else if (!selectedCards.learner && formData.navigate_to === 'Wallet') {
      setFormData(prev => ({ ...prev, navigate_to: '' }));
    }
  }, [selectedCards.learner, navigationType]);

  // Fetch a single page of subjects, optionally filtered by subject type / search
  const fetchSubjects = useCallback(
    async (page = 1, size = SUBJECT_PAGE_SIZE, subjectType = '', search = '') => {
      try {
        const params = new URLSearchParams({ page: String(page), size: String(size) });
        if (subjectType) {
          params.append('subject_type', subjectType);
        }
        if (search) {
          params.append('search_title', search);
        }
        const { data } = await axiosInstance.get(`/common/subjects?${params.toString()}`);
        return data || { results: [], count: 0 };
      } catch (err) {
        console.error('Error fetching subjects:', err);
        return { results: [], count: 0 };
      }
    },
    []
  );

  // Debounce the subjects search input so we don't hit the API on every keystroke
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSubjectSearch(subjectSearch.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [subjectSearch]);

  // Load the first page of subjects whenever the instructor type or search term
  // changes so the list always reflects the current filters.
  useEffect(() => {
    const loadSubjects = async () => {
      const subjectType = subjectTypeApiMap[instructorType] || '';
      const { results, count } = await fetchSubjects(
        1,
        SUBJECT_PAGE_SIZE,
        subjectType,
        debouncedSubjectSearch
      );
      const list = results || [];
      setSubjects(list);
      setSubjectPage(1);
      setSubjectTotalCount(count || 0);
      setSubjectHasMore(list.length < (count || 0));
    };

    loadSubjects();
  }, [instructorType, debouncedSubjectSearch, fetchSubjects]);

  // Load the next page of subjects (triggered by scrolling the dropdown)
  const loadMoreSubjects = useCallback(async () => {
    if (subjectLoadingMore || !subjectHasMore) return;
    setSubjectLoadingMore(true);
    try {
      const nextPage = subjectPage + 1;
      const subjectType = subjectTypeApiMap[instructorType] || '';
      const { results: nextSubjects, count } = await fetchSubjects(
        nextPage,
        SUBJECT_PAGE_SIZE,
        subjectType,
        debouncedSubjectSearch
      );
      const total = count || subjectTotalCount;
      setSubjectTotalCount(total);
      setSubjects(prev => {
        const existingIds = new Set(prev.map(s => s.id));
        const merged = [...prev];
        (nextSubjects || []).forEach(s => {
          if (!existingIds.has(s.id)) merged.push(s);
        });
        setSubjectHasMore(merged.length < total);
        return merged;
      });
      setSubjectPage(nextPage);
    } catch (e) {
      console.error('Error loading more subjects:', e);
    } finally {
      setSubjectLoadingMore(false);
    }
  }, [
    subjectLoadingMore,
    subjectHasMore,
    subjectPage,
    subjectTotalCount,
    instructorType,
    debouncedSubjectSearch,
    fetchSubjects,
  ]);

  // Infinite-scroll handler for the subjects dropdown menu
  const handleSubjectMenuScroll = event => {
    const list = event.currentTarget;
    if (!list) return;
    const nearBottom = list.scrollTop + list.clientHeight >= list.scrollHeight - 8;
    if (nearBottom) {
      loadMoreSubjects();
    }
  };

  // Fetch countries list on mount
  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await axiosInstance.get('/auth/countries');
        setCountries(response.data || []);
      } catch (err) {
        console.error('Error fetching countries:', err);
      }
    };

    fetchCountries();
  }, []);

  // Fetch cities whenever countries selection changes
  useEffect(() => {
    const fetchCities = async () => {
      if (selectedCountries.length === 0) {
        setCityOptions([]);
        return;
      }

      try {
        // Fetch cities for all selected countries and merge results
        const allCityPromises = selectedCountries.map(c =>
          axiosInstance.get(`/auth/countries/${c.id}/cities`).then(res => res.data || [])
        );
        const results = await Promise.all(allCityPromises);
        const merged = results.flat();
        setCityOptions(merged);
      } catch (err) {
        console.error('Error fetching cities:', err);
        setCityOptions([]);
      }
    };

    fetchCities();
  }, [selectedCountries]);

  // Handler for target audience card selection
  const handleCardClick = cardType => {
    if (cardType === 'instructor' && selectedCards.learner) {
      // If learner is selected, deselect it when instructor is clicked
      setSelectedCards(prev => ({
        ...prev,
        learner: false,
        instructor: !prev.instructor,
      }));
    } else if (cardType === 'learner' && selectedCards.instructor) {
      // If instructor is selected, deselect it when learner is clicked
      setSelectedCards(prev => ({
        ...prev,
        instructor: false,
        learner: !prev.learner,
      }));
      // Clear instructor-related selections
      setInstructorType('');
      setSelectedLevels([]);
      setSelectedSubjects([]);
    } else {
      // Toggle the selected card
      setSelectedCards(prev => ({
        ...prev,
        [cardType]: !prev[cardType],
      }));
    }

    // Clear related selections when card is deselected
    if (selectedCards[cardType]) {
      if (cardType === 'instructor') {
        setInstructorType('');
        setSelectedLevels([]);
        setSelectedSubjects([]);
      } else if (cardType === 'country') {
        setSelectedCountries([]);
        setSelectedCities([]);
        setSelectedCards(prev => ({ ...prev, city: false }));
      } else if (cardType === 'city') {
        setSelectedCities([]);
      } else if (cardType === 'gender') {
        setSelectedGender('');
      }
    }
  };

  // Handler for instructor type selection
  const handleInstructorTypeChange = type => {
    const normalizedType = type === '' ? '' : type;
    setInstructorType(normalizedType);
    // Subjects are scoped to the instructor type, so reset the current
    // selection and search — the subjects list is refetched for the new type.
    setSelectedSubjects([]);
    setSubjectSearch('');
    if (!normalizedType) {
      setSelectedLevels([]);
      return;
    }
    const allowedLevels = getLevelsForInstructorType(normalizedType);
    setSelectedLevels(prev => prev.filter(level => allowedLevels.includes(level)));
  };

  // Handler for subject selection
  const handleSubjectChange = event => {
    const value = event.target.value;
    setSelectedSubjects(value);
  };

  // Handler for level selection
  const handleLevelChange = event => {
    if (!instructorType) {
      toast.error('Please select Instructor Type first');
      return;
    }
    const value = event.target.value;
    const allowedLevels = getLevelsForInstructorType(instructorType);
    const filtered = Array.isArray(value) ? value.filter(l => allowedLevels.includes(l)) : [];
    setSelectedLevels(filtered);
  };

  // Handler for country selection
  const handleCountryChange = event => {
    const value = event.target.value;
    setSelectedCountries(value);
  };

  // Handler for city selection
  const handleCityChange = event => {
    const value = event.target.value;
    setSelectedCities(value);
  };

  // Handler for gender selection
  const handleGenderChange = gender => {
    setSelectedGender(gender === '' ? '' : gender);
  };

  // Remove selected items
  const removeSelectedItem = (type, item) => {
    switch (type) {
      case 'instructor_type':
        setInstructorType('');
        break;
      case 'level':
        setSelectedLevels(prev => prev.filter(l => l !== item));
        break;
      case 'subject':
        setSelectedSubjects(prev => prev.filter(s => s.id !== item.id));
        break;
      case 'country':
        setSelectedCountries(prev => prev.filter(c => c.id !== item.id));
        break;
      case 'city':
        setSelectedCities(prev => prev.filter(c => c.id !== item.id));
        break;
      case 'gender':
        setSelectedGender('');
        break;
      default:
        break;
    }
  };

  const resetBannerFileInput = () => {
    const fileInput = document.getElementById('bannerImage');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleChange = e => {
    const { name, value, type, files } = e.target;
    if (type === 'file' && files && files.length > 0) {
      // Open the cropper instead of uploading the raw file. The admin frames
      // the image into the exact mobile banner ratio and only the cropped
      // result is uploaded.
      const objectUrl = URL.createObjectURL(files[0]);
      setCropSource(objectUrl);
      // Allow re-selecting the same file later.
      resetBannerFileInput();
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Discard the chosen source image and close the cropper.
  const handleCropCancel = () => {
    if (cropSource) {
      URL.revokeObjectURL(cropSource);
    }
    setCropSource('');
  };

  // Receive the cropped blob from the cropper, upload it, then close.
  const handleCropApply = async blob => {
    const croppedFile = new File([blob], 'banner.jpg', { type: 'image/jpeg' });
    if (cropSource) {
      URL.revokeObjectURL(cropSource);
    }
    setCropSource('');
    await uploadImage(croppedFile);
  };

  // Function to handle image upload
  const uploadImage = async file => {
    setImageUploading(true);

    try {
      const imageFormData = new FormData();
      imageFormData.append('file', file);

      const imageResponse = await axiosInstance.post('/admin/banners/image', imageFormData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Set the image URL from the response
      const imageUrl = imageResponse.data;
      if (!imageUrl) {
        throw new Error('No image URL received from server');
      }

      setFormData(prevState => ({
        ...prevState,
        image: file,
        image_url: imageUrl,
      }));
      resetBannerFileInput();
    } catch (err) {
      console.error('Error uploading image:', err);
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        err.message ||
        'Failed to upload image. Please try again.';
      toast.error(errorMessage);
    } finally {
      setImageUploading(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Optional: Validate URL format if provided
    if (navigationType === 'url' && customUrl.trim()) {
      try {
        new URL(customUrl.trim());
      } catch (e) {
        toast.error('Please enter a valid URL');
        return;
      }
    }

    if (!formData.image_url) {
      toast.error('Please upload an image (required)');
      return;
    }

    if (!formData.expiry_date) {
      toast.error('Please select an expiry date (required)');
      return;
    }

    setLoading(true);

    try {
      // Create the banner with the already uploaded image URL
      const bannerData = {
        title: formData.title?.trim() || null,
        placement: formData.placement?.trim() || null,
        image_url: formData.image_url,
        expiry_date: formData.expiry_date ? `${formData.expiry_date}T00:00:00` : null,
        user_type: null,
        instructor_type: null,
        gender: null,
        level_of_teachings: null,
        subjects: null,
        countries: null,
        cities: null,
        navigate_to: null,
      };

      // Optional: Add navigation if provided
      if (navigationType === 'url' && customUrl.trim()) {
        bannerData.navigate_to = customUrl;
      } else if (navigationType === 'inapp' && formData.navigate_to) {
        bannerData.navigate_to = formData.navigate_to;
      }

      // Optional: Add user_type based on selected filters
      if (selectedCards.learner) {
        bannerData.user_type = 'student';
      } else if (selectedCards.instructor) {
        bannerData.user_type = 'teacher';
      }

      // Optional: Process filters and add them to the request body
      if (selectedCards.instructor) {
        if (instructorType) {
          bannerData.instructor_type =
            instructorTypeApiMap[instructorType] ||
            instructorType.toLowerCase().replace(/\s+/g, '_');
        }
        if (selectedLevels.length > 0) {
          bannerData.level_of_teachings = selectedLevels;
        }
        if (selectedSubjects.length > 0) {
          bannerData.subjects = selectedSubjects.map(subject => subject.id);
        }
      }

      // Optional: Gender filter
      if (selectedCards.gender && selectedGender) {
        bannerData.gender = selectedGender.toLowerCase();
      }

      // Optional: Country & City filters
      if (selectedCards.country && selectedCountries.length > 0) {
        bannerData.countries = selectedCountries.map(c => c.id);
      }

      if (selectedCards.city && selectedCities.length > 0) {
        bannerData.cities = selectedCities.map(c => c.id);
      }

      console.log('Creating banner with data:', bannerData);

      const response = await axiosInstance.post('/admin/banners', bannerData);

      // Show success message
      toast.success('Banner created successfully!');

      if (onSuccess) {
        onSuccess(response.data);
      } else {
        onClose();
      }
    } catch (err) {
      console.error('Error creating banner:', err);

      // Extract detailed error message from backend response
      let errorMessage = 'Failed to create banner. Please try again.';

      if (err.response?.data) {
        const errorData = err.response.data;

        // Check for different error response formats
        if (errorData.detail) {
          errorMessage = errorData.detail;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        } else if (typeof errorData === 'string') {
          errorMessage = errorData;
        }

        // Add error code if available
        if (errorData.error_code) {
          errorMessage = `[${errorData.error_code}] ${errorMessage}`;
        }

        // Add status code if available
        if (errorData.status_code) {
          errorMessage = `[${errorData.status_code}] ${errorMessage}`;
        }
      }

      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = () => {
    if (!imageUploading) {
      resetBannerFileInput();
      document.getElementById('bannerImage').click();
    }
  };

  const handleRemoveImage = e => {
    e.stopPropagation();
    resetBannerFileInput();
    setFormData({
      ...formData,
      image: null,
      image_url: '',
    });
  };

  // Handle navigation type change
  const handleNavigationTypeChange = type => {
    setNavigationType(type);
    if (type === 'url') {
      setFormData({ ...formData, navigate_to: customUrl });
    } else if (type === 'inapp') {
      setCustomUrl('');
      setFormData({ ...formData, navigate_to: formData.navigate_to });
    } else {
      // type is empty string - clear everything
      setCustomUrl('');
      setFormData({ ...formData, navigate_to: '' });
    }
  };

  // Handle in-app navigation selection
  const handleInAppNavigation = page => {
    setFormData({ ...formData, navigate_to: page === '' ? '' : page });
  };

  // Handle custom URL change
  const handleCustomUrlChange = e => {
    const url = e.target.value;
    setCustomUrl(url);
    setFormData({ ...formData, navigate_to: url });
  };

  // Handle calendar popover
  const handleCalendarClick = event => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCalendarClose = () => {
    setCalendarAnchorEl(null);
  };

  const handleDateChange = date => {
    setFormData({ ...formData, expiry_date: date });
    handleCalendarClose();
  };

  // Format date for display
  const formatDateForDisplay = dateString => {
    if (!dateString) return '';
    // Fix: Parse date string as local date to avoid timezone issues
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day); // month is 0-indexed
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const open = Boolean(calendarAnchorEl);

  const isFormValid = Boolean(formData.image_url && formData.expiry_date);

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <Box className="modal-title">Create Banner</Box>
          <button className="close-btn" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <div className="divider"></div>
        <form onSubmit={handleSubmit} className="modal-form">
          <div className="modal-content-banner">
            <input
              type="file"
              id="bannerImage"
              name="image"
              accept="image/*"
              onChange={handleChange}
              style={{ display: 'none' }}
              disabled={imageUploading}
            />

            <div className="form-field">
              <Box className="form-label">Mobile app preview</Box>
              <MobileBannerPreview
                imageUrl={formData.image_url}
                title={formData.title}
                placement={formData.placement}
                onUploadRequest={handleFileUpload}
                onRemove={handleRemoveImage}
                isUploading={imageUploading}
                uploadIcon={BannerIcon}
              />
            </div>

            <div className="image-note">
              <p>
                Upload any image, then zoom and drag to fit the frame. This preview is exactly how
                it appears in the app. Use a wide image (1200px or wider) for best quality.
              </p>
            </div>

            <div className="form-field">
              <Box className="form-label">Title (Optional)</Box>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter banner title"
                className="form-input"
              />
            </div>

            {/* Placement field moved below title */}
            <div className="form-field">
              <Box className="form-label">Description (Optional)</Box>
              <input
                type="text"
                name="placement"
                className="form-input"
                value={formData.placement}
                onChange={handleChange}
                placeholder="Enter description"
              />
            </div>

            {/* Target Audience Section */}
            <div className="form-field">
              <Box className="form-label">Send to (Optional)</Box>

              {/* Target Audience Cards */}
              <div className="banner-target-audience-cards">
                <div
                  className={`banner-audience-card ${selectedCards.instructor ? 'selected' : ''} ${selectedCards.learner ? 'disabled' : ''}`}
                  onClick={() => handleCardClick('instructor')}
                  title={
                    selectedCards.learner
                      ? 'Cannot select both Instructor and Learner'
                      : 'Select instructor filters'
                  }
                >
                  <div className="banner-card-text">Instructor</div>
                </div>

                <div
                  className={`banner-audience-card ${selectedCards.learner ? 'selected' : ''} ${selectedCards.instructor ? 'disabled' : ''}`}
                  onClick={() => handleCardClick('learner')}
                  title={
                    selectedCards.instructor
                      ? 'Cannot select both Instructor and Learner'
                      : 'Select learner'
                  }
                >
                  <div className="banner-card-text">Learner</div>
                </div>

                <div
                  className={`banner-audience-card ${selectedCards.gender ? 'selected' : ''}`}
                  onClick={() => handleCardClick('gender')}
                  title="Select gender filter"
                >
                  <div className="banner-card-text">Gender</div>
                </div>

                <div
                  className={`banner-audience-card ${selectedCards.country ? 'selected' : ''}`}
                  onClick={() => handleCardClick('country')}
                  title="Select countries"
                >
                  <div className="banner-card-text">Country</div>
                </div>

                <div
                  className={`banner-audience-card ${selectedCards.city ? 'selected' : ''} ${!selectedCards.country ? 'disabled' : ''}`}
                  onClick={() => {
                    if (selectedCards.country) {
                      handleCardClick('city');
                    }
                  }}
                  title={!selectedCards.country ? 'Please select a country first' : 'Select cities'}
                >
                  <div className="banner-card-text">City</div>
                </div>
              </div>

              {/* Instructor Dropdowns */}
              {selectedCards.instructor && (
                <div className="banner-instructor-dropdowns">
                  {/* Instructor Type */}
                  <div className="banner-dropdown-section">
                    <Box className="banner-dropdown-label">Instructor Type (Optional)</Box>
                    <FormControl fullWidth>
                      <Select
                        value={instructorType}
                        onChange={e => handleInstructorTypeChange(e.target.value)}
                        displayEmpty
                        className="banner-form-input"
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={selected => selected || 'Select Instructor Type'}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value="">Select Instructor Type</MenuItem>
                        {filterOptions.instructor_type.map(type => (
                          <MenuItem key={type} value={type}>
                            {type}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {/* Levels to Teach */}
                  <div className="banner-dropdown-section">
                    <Box className="banner-dropdown-label">Levels to Teach (Optional)</Box>
                    <FormControl fullWidth>
                      <Select
                        multiple
                        value={selectedLevels}
                        onChange={handleLevelChange}
                        displayEmpty
                        className="banner-form-input"
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={selected => {
                          if (!instructorType) {
                            return 'Please select Instructor Type first';
                          }
                          if (selected.length === 0) {
                            return 'Select Levels';
                          }
                          if (selected.length <= 3) {
                            return selected.map(level => formatText(level)).join(', ');
                          }
                          const firstThree = selected
                            .slice(0, 3)
                            .map(level => formatText(level))
                            .join(', ');
                          const remainingCount = selected.length - 3;
                          return `${firstThree}... (+${remainingCount} more)`;
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {getLevelsForInstructorType(instructorType).map(level => (
                          <MenuItem key={level} value={level}>
                            <Checkbox checked={selectedLevels.includes(level)} />
                            {formatText(level)}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </div>

                  {/* Subjects */}
                  <div className="banner-dropdown-section">
                    <Box className="banner-dropdown-label">Subjects (Optional)</Box>
                    <FormControl fullWidth>
                      <Select
                        multiple
                        value={selectedSubjects}
                        onChange={handleSubjectChange}
                        displayEmpty
                        className="banner-form-input"
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={selected => {
                          if (selected.length === 0) {
                            return 'Select Subjects';
                          }
                          if (selected.length <= 3) {
                            return selected.map(subject => subject.title).join(', ');
                          } else {
                            const firstThree = selected
                              .slice(0, 3)
                              .map(subject => subject.title)
                              .join(', ');
                            const remainingCount = selected.length - 3;
                            return `${firstThree}... (+${remainingCount} more)`;
                          }
                        }}
                        MenuProps={{
                          autoFocus: false,
                          // Open the menu below the input instead of over it so the
                          // search box doesn't overlap the select trigger.
                          anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                          transformOrigin: { vertical: 'top', horizontal: 'left' },
                          PaperProps: {
                            // The Paper is the actual scroll container (maxHeight +
                            // overflow), so the scroll listener must live here.
                            onScroll: handleSubjectMenuScroll,
                            style: {
                              maxHeight: 240,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <ListSubheader
                          disableSticky
                          sx={{ backgroundColor: '#fff', px: 1.5, py: 1 }}
                        >
                          <TextField
                            autoFocus
                            fullWidth
                            size="small"
                            placeholder="Search subjects..."
                            value={subjectSearch}
                            onChange={e => setSubjectSearch(e.target.value)}
                            onKeyDown={e => e.stopPropagation()}
                          />
                        </ListSubheader>
                        {subjects.map(subject => (
                          <MenuItem key={subject.id} value={subject}>
                            <Checkbox checked={selectedSubjects.some(s => s.id === subject.id)} />
                            {subject.title}
                          </MenuItem>
                        ))}
                        {subjects.length === 0 && !subjectLoadingMore && (
                          <MenuItem disabled value="">
                            No subjects found
                          </MenuItem>
                        )}
                        {subjectLoadingMore && (
                          <MenuItem disabled value="">
                            Loading more...
                          </MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </div>
                </div>
              )}

              {/* Gender Dropdown */}
              {selectedCards.gender && (
                <div className="banner-gender-dropdown">
                  <Box className="banner-dropdown-label">Gender (Optional)</Box>
                  <FormControl fullWidth>
                    <Select
                      value={selectedGender}
                      onChange={e => handleGenderChange(e.target.value)}
                      displayEmpty
                      className="banner-form-input"
                      IconComponent={KeyboardArrowDownIcon}
                      renderValue={selected => {
                        if (!selected) return 'Select Gender';
                        return selected.charAt(0).toUpperCase() + selected.slice(1);
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            overflowY: 'auto',
                          },
                        },
                      }}
                    >
                      <MenuItem value="">Select Gender</MenuItem>
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                    </Select>
                  </FormControl>
                </div>
              )}

              {/* Country Dropdown */}
              {selectedCards.country && (
                <div className="banner-dropdown-section">
                  <Box className="banner-dropdown-label">Country (Optional)</Box>
                  <FormControl fullWidth>
                    <Select
                      multiple
                      value={selectedCountries}
                      onChange={handleCountryChange}
                      displayEmpty
                      className="banner-form-input"
                      IconComponent={KeyboardArrowDownIcon}
                      renderValue={selected => {
                        if (selected.length === 0) {
                          return 'Select countries';
                        }
                        if (selected.length <= 5) {
                          return selected.map(country => country.name).join(', ');
                        } else {
                          const firstFive = selected
                            .slice(0, 5)
                            .map(country => country.name)
                            .join(', ');
                          const remainingCount = selected.length - 5;
                          return `${firstFive}... (+${remainingCount} more)`;
                        }
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 200,
                            overflowY: 'auto',
                          },
                        },
                      }}
                    >
                      {countries.map(country => (
                        <MenuItem key={country.id} value={country}>
                          <Checkbox checked={selectedCountries.includes(country)} />
                          {country.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}

              {/* City Dropdown */}
              {selectedCards.city && (
                <div className="banner-city-dropdown">
                  <Box className="banner-dropdown-label">City (Optional)</Box>
                  {!selectedCards.country || selectedCountries.length === 0 ? (
                    <div className="banner-city-note">
                      <p>⚠️ Please select a country first to choose cities</p>
                    </div>
                  ) : (
                    <FormControl fullWidth>
                      <Select
                        multiple
                        value={selectedCities}
                        onChange={handleCityChange}
                        displayEmpty
                        className="banner-form-input"
                        IconComponent={KeyboardArrowDownIcon}
                        renderValue={selected => {
                          if (selected.length === 0) {
                            return 'Select cities';
                          }
                          if (selected.length <= 5) {
                            return selected.map(city => city.name).join(', ');
                          } else {
                            const firstFive = selected
                              .slice(0, 5)
                              .map(city => city.name)
                              .join(', ');
                            const remainingCount = selected.length - 5;
                            return `${firstFive}... (+${remainingCount} more)`;
                          }
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        {cityOptions.map(city => (
                          <MenuItem key={city.id} value={city}>
                            <Checkbox checked={selectedCities.includes(city)} />
                            {city.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </div>
              )}

              {/* Selected Items Display - Only show if there are selected items */}
              {((selectedCards.instructor &&
                (instructorType || selectedLevels.length > 0 || selectedSubjects.length > 0)) ||
                (selectedCards.gender && selectedGender) ||
                (selectedCards.country && selectedCountries.length > 0) ||
                (selectedCards.city && selectedCities.length > 0)) && (
                <div className="banner-selected-items-display">
                  {/* Instructor Type */}
                  {selectedCards.instructor && instructorType && (
                    <Chip
                      label={`Instructor Type: ${instructorType}`}
                      onDelete={() => removeSelectedItem('instructor_type')}
                      className="banner-selected-item-chip"
                    />
                  )}

                  {/* Levels */}
                  {selectedCards.instructor &&
                    selectedLevels.map(level => (
                      <Chip
                        key={level}
                        label={`Level: ${formatText(level)}`}
                        onDelete={() => removeSelectedItem('level', level)}
                        className="banner-selected-item-chip"
                      />
                    ))}

                  {/* Subjects */}
                  {selectedCards.instructor &&
                    selectedSubjects.map(subject => (
                      <Chip
                        key={subject.id}
                        label={`Subject: ${subject.title}`}
                        onDelete={() => removeSelectedItem('subject', subject)}
                        className="banner-selected-item-chip"
                      />
                    ))}

                  {/* Gender */}
                  {selectedCards.gender && selectedGender && (
                    <Chip
                      label={`Gender: ${selectedGender.charAt(0).toUpperCase() + selectedGender.slice(1)}`}
                      onDelete={() => removeSelectedItem('gender')}
                      className="banner-selected-item-chip"
                    />
                  )}

                  {/* Countries */}
                  {selectedCards.country &&
                    selectedCountries.map(country => (
                      <Chip
                        key={country.id}
                        label={`Country: ${country.name}`}
                        onDelete={() => removeSelectedItem('country', country)}
                        className="banner-selected-item-chip"
                      />
                    ))}

                  {/* Cities */}
                  {selectedCards.city &&
                    selectedCities.map(city => (
                      <Chip
                        key={city.id}
                        label={`City: ${city.name}`}
                        onDelete={() => removeSelectedItem('city', city)}
                        className="banner-selected-item-chip"
                      />
                    ))}
                </div>
              )}
            </div>

            <div className="form-field">
              <Box className="form-label">Navigate to (Optional)</Box>

              {/* Navigation Type Selection */}
              <FormControl fullWidth style={{ marginBottom: '16px' }}>
                <Select
                  value={navigationType}
                  onChange={e => handleNavigationTypeChange(e.target.value)}
                  displayEmpty
                  className="banner-form-input"
                  IconComponent={KeyboardArrowDownIcon}
                  renderValue={selected =>
                    selected
                      ? selected === 'inapp'
                        ? 'In App'
                        : 'URL'
                      : 'Select Navigation Type (Optional)'
                  }
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                        overflowY: 'auto',
                      },
                    },
                  }}
                >
                  <MenuItem value="">Select Navigation Type (Optional)</MenuItem>
                  <MenuItem value="inapp">In App</MenuItem>
                  <MenuItem value="url">URL</MenuItem>
                </Select>
              </FormControl>

              {/* In App Navigation Options */}
              {navigationType === 'inapp' && (
                <FormControl fullWidth>
                  <Select
                    value={formData.navigate_to}
                    onChange={e => handleInAppNavigation(e.target.value)}
                    displayEmpty
                    className="banner-form-input"
                    IconComponent={KeyboardArrowDownIcon}
                    renderValue={selected => selected || 'Select Page (Optional)'}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          overflowY: 'auto',
                        },
                      },
                    }}
                  >
                    <MenuItem value="">Select Page (Optional)</MenuItem>
                    <MenuItem value="Profile">Profile</MenuItem>
                    <MenuItem value="Schedule">Schedule</MenuItem>
                    <MenuItem value={inAppThirdPageOption}>{inAppThirdPageOption}</MenuItem>
                  </Select>
                </FormControl>
              )}

              {/* Custom URL Input */}
              {navigationType === 'url' && (
                <input
                  type="url"
                  value={customUrl}
                  onChange={handleCustomUrlChange}
                  placeholder="Enter URL (https://example.com) - Optional"
                  className="banner-form-input"
                />
              )}
            </div>

            <div className="form-field">
              <Box className="form-label">Expiry Date *</Box>
              <div className="banner-date-picker-container">
                <div className="banner-date-input-wrapper" onClick={handleCalendarClick}>
                  <input
                    type="text"
                    value={formatDateForDisplay(formData.expiry_date)}
                    placeholder="Select date"
                    className="banner-form-input banner-date-display-input"
                    readOnly
                  />
                  <Calendar
                    size="20"
                    color="#9ca3af"
                    variant="Outline"
                    className="banner-calendar-icon"
                  />
                </div>

                <Popover
                  open={open}
                  anchorEl={calendarAnchorEl}
                  onClose={handleCalendarClose}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  PaperProps={{
                    elevation: 8,
                    sx: {
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      mt: 1,
                    },
                  }}
                >
                  <CustomCalendar
                    value={formData.expiry_date}
                    onChange={handleDateChange}
                    minDate={(() => {
                      const today = new Date();
                      const year = today.getFullYear();
                      const month = String(today.getMonth() + 1).padStart(2, '0');
                      const day = String(today.getDate()).padStart(2, '0');
                      return `${year}-${month}-${day}`;
                    })()}
                  />
                </Popover>
              </div>
            </div>

            {/* Remove the error message display since we're using toast notifications */}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="cancel-btn"
              onClick={onClose}
              disabled={loading || imageUploading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="save-btn"
              disabled={loading || imageUploading || !isFormValid}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </div>
        </form>
      </div>

      {cropSource ? (
        <BannerImageCropper
          imageSrc={cropSource}
          title={formData.title}
          placement={formData.placement}
          onCancel={handleCropCancel}
          onApply={handleCropApply}
        />
      ) : null}
    </div>
  );
};

export default CreateNewBannerModel;
